import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import useApi from "../../shared/hooks/useApi";
import { OfficeId } from "../../shared/schema/schema";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import SettingsTable from "./components/SettingsTable";
import ErrorState from "./components/ErrorState";
import LoadingState from "./components/LoadingState";
import {
  Certification,
  CertificationBroadcastSetting,
  DraftPermittedMap,
  NewSettingState,
} from "./certification-broadcast-settings.types";

const emptyNewSetting: NewSettingState = {
  officeId: null,
  primaryCertification: null,
  permittedCertifications: [],
};

export default function CertificationBroadcastSettingsTable() {
  const { api, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const officesQuery = useQuery(queries.office.list());
  const certificationsQuery = useQuery(queries.certifications.certificationsSettings());

  const officeIds = React.useMemo(
    () => (officesQuery.data?.offices ?? []).map((office) => office.id),
    [officesQuery.data]
  );

  const settingsQuery = useQuery({
    queryKey: ["certification-broadcast-settings", officeIds],
    enabled: officesQuery.isSuccess,
    queryFn: async () => {
      if (officeIds.length === 0) {
        return [] as CertificationBroadcastSetting[];
      }

      const responses = await Promise.all(
        officeIds.map((officeId) =>
          api.get("./offices/:officeId/certification_broadcast_settings", {
            path: { officeId },
          })
        )
      );

      return responses.flatMap((response) => response.settings);
    },
  });

  const offices = React.useMemo(
    () => officesQuery.data?.offices ?? [],
    [officesQuery.data?.offices]
  );

  const officeNameMap = React.useMemo(() => {
    return new Map(offices.map((office) => [office.id, office.name]));
  }, [offices]);

  const certificationOptions = React.useMemo(() => {
    const activeCertifications =
      certificationsQuery.data?.certifications
        ?.filter((cert) => cert.isActive)
        .map((cert) => cert.certification) ?? [];

    const settingCertifications = (settingsQuery.data ?? []).flatMap((setting) => [
      setting.primaryCertification,
      ...setting.permittedCertifications,
    ]);

    const unique = Array.from(new Set([...activeCertifications, ...settingCertifications]));

    return unique
      .sort((left, right) => String(left).localeCompare(String(right)))
      .map((certification) => ({
        value: certification,
        label: String(certification),
      }));
  }, [certificationsQuery.data, settingsQuery.data]);

  const [draftPermitted, setDraftPermitted] = React.useState<DraftPermittedMap>({});

  React.useEffect(() => {
    if (!settingsQuery.data) {
      return;
    }

    setDraftPermitted((current) => {
      const next = { ...current };
      const ids = new Set(settingsQuery.data.map((setting) => String(setting.id)));

      settingsQuery.data.forEach((setting) => {
        const key = String(setting.id);
        if (next[key] === undefined) {
          next[key] = setting.permittedCertifications ?? [];
        }
      });

      Object.keys(next).forEach((key) => {
        if (!ids.has(key)) {
          delete next[key];
        }
      });

      return next;
    });
  }, [settingsQuery.data]);

  const [newSetting, setNewSetting] = React.useState<NewSettingState>(emptyNewSetting);

  const createMutation = useMutation({
    mutationFn: (payload: {
      officeId: OfficeId;
      primaryCertification: Certification;
      permittedCertifications: Certification[];
    }) =>
      api.post("./offices/:officeId/certification_broadcast_settings", {
        path: { officeId: payload.officeId },
        body: {
          primaryCertification: payload.primaryCertification,
          permittedCertifications: payload.permittedCertifications,
        },
      }),
    onSuccess: () => {
      setNewSetting(emptyNewSetting);
      queryClient.invalidateQueries({ queryKey: ["certification-broadcast-settings"] });
      toast({ status: "success", description: "Setting created" });
    },
    onError: (error) => {
      toast({ status: "error", description: formatErrorResponse(error) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: {
      officeId: OfficeId;
      settingId: CertificationBroadcastSetting["id"];
      permittedCertifications: Certification[];
    }) =>
      api.put("./offices/:officeId/certification_broadcast_settings/:settingId", {
        path: {
          officeId: payload.officeId,
          settingId: payload.settingId,
        },
        body: {
          permittedCertifications: payload.permittedCertifications,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certification-broadcast-settings"] });
      toast({ status: "success", description: "Setting updated" });
    },
    onError: (error) => {
      toast({ status: "error", description: formatErrorResponse(error) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (payload: { officeId: OfficeId; settingId: CertificationBroadcastSetting["id"] }) =>
      api.delete("./offices/:officeId/certification_broadcast_settings/:settingId", {
        path: {
          officeId: payload.officeId,
          settingId: payload.settingId,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certification-broadcast-settings"] });
      toast({ status: "success", description: "Setting deleted" });
    },
    onError: (error) => {
      toast({ status: "error", description: formatErrorResponse(error) });
    },
  });

  const isLoading =
    officesQuery.isLoading || certificationsQuery.isLoading || settingsQuery.isLoading;

  const error = officesQuery.error ?? certificationsQuery.error ?? settingsQuery.error;

  const settings = settingsQuery.data ?? [];

  const officeOptions = offices
    .map((office) => ({ value: office.id, label: office.name }))
    .sort((left, right) => left.label.localeCompare(right.label));

  const hasDuplicateCombination =
    newSetting.officeId !== null &&
    newSetting.primaryCertification !== null &&
    settings.some(
      (setting) =>
        setting.officeId === newSetting.officeId &&
        setting.primaryCertification === newSetting.primaryCertification
    );

  const canCreate =
    newSetting.officeId !== null &&
    newSetting.primaryCertification !== null &&
    newSetting.permittedCertifications.length > 0 &&
    !hasDuplicateCombination;

  const handleCreate = () => {
    if (hasDuplicateCombination) {
      toast({
        status: "error",
        description: "That office already has this certification setting.",
      });
      return;
    }

    if (!canCreate) {
      return;
    }

    createMutation.mutate({
      officeId: newSetting.officeId as OfficeId,
      primaryCertification: newSetting.primaryCertification as Certification,
      permittedCertifications: newSetting.permittedCertifications,
    });
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={formatErrorResponse(error)} />;
  }

  return (
    <SettingsTable
      certificationOptions={certificationOptions}
      createDisabled={!canCreate || createMutation.isPending}
      createLoading={createMutation.isPending}
      createTitle={
        hasDuplicateCombination
          ? "This office already has a setting for that certification"
          : undefined
      }
      deleteLoading={deleteMutation.isPending}
      draftPermitted={draftPermitted}
      newSetting={newSetting}
      officeNameMap={officeNameMap}
      officeOptions={officeOptions}
      settings={settings}
      updateLoading={updateMutation.isPending}
      onChangeDraftPermitted={(settingId, value) =>
        setDraftPermitted((current) => ({
          ...current,
          [String(settingId)]: value ?? [],
        }))
      }
      onChangeNewSetting={(changes) =>
        setNewSetting((current) => ({
          ...current,
          ...changes,
        }))
      }
      onCreate={handleCreate}
      onDelete={(setting) =>
        deleteMutation.mutate({
          officeId: setting.officeId,
          settingId: setting.id,
        })
      }
      onUpdate={(setting, permittedCertifications) =>
        updateMutation.mutate({
          officeId: setting.officeId,
          settingId: setting.id,
          permittedCertifications,
        })
      }
    />
  );
}
